import { useId } from 'react'

import { ShadowFilter } from './ShadowFilter'

export type AccessibleSVGProps = {
  label: string
  circle?: boolean
  circleProps?: React.SVGProps<SVGCircleElement>
  shadow?: boolean
  shadowProps?: React.SVGProps<SVGFEDropShadowElement>
  children?: React.ReactNode | ((shadowFilter: string) => React.ReactNode)
} & Omit<React.SVGProps<SVGSVGElement>, 'children'>

/**
 * Accessible SVG component providing a title element for
 * screen readers and a shadow filter.
 *
 * Note:
 * To apply the shadow, pass a function as `children` and spread the received
 * value onto the `filter` attribute of the element/path:
 * `{(shadowFilter) => <path filter={shadowFilter} ... />}`. The filter id is
 * generated with `useId()` so it stays unique across icon instances.
 * Label is required for accessibility.
 * If the icon truly doesn't need a label, use an empty string and make the icon aria-hidden
 */
export const AccessibleSVG: React.FC<AccessibleSVGProps> = ({
  children,
  label,
  circle,
  circleProps,
  shadow,
  shadowProps,
  ...props
}) => {
  const shadowFilterId = `filter-${useId()}`
  return (
    <svg role="img" {...props}>
      {label !== undefined && <title>{label}</title>}
      {circle && (
        <circle
          cx="50%"
          cy="50%"
          r="50%"
          fill="rgba(64 64 64 / 50%)"
          {...circleProps}
        />
      )}
      {shadow && <ShadowFilter {...shadowProps} id={shadowFilterId} />}
      {typeof children === 'function'
        ? children(shadow ? `url(#${shadowFilterId})` : '')
        : children}
    </svg>
  )
}
